import { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { User } from '@img-arena/consoles-api-streaming'
import { DataCard } from '@/components/card'
import { DownloadCsv } from '@/components/DownloadCsv'
import { UserFormButton } from '@/components/forms/UserForm/UserForm'
import { DGEUserForm } from '@/components/forms/UserForm/UserForm.dge'
import {
  ActionDeleteStreamingUserItem,
  ActionFormItem,
  ActionsMenu
} from '@/components/table/actions'
import { FilterableTable } from '@/components/table/base'
import { CheckboxDeleteSelectedStreamingUserItem, CheckboxMenu } from '@/components/table/checkbox'
import { STORE_STATUS } from '@/constants/store'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { streamingRestApi } from '@/service/streamingApi'
import { useUsersStoreDGE } from '@/stores/users'

import { DGE_TABLE_COLUMNS, DGE_TABLE_FILTERS } from './users.dge.table'

const usersDGECsvHeaders = [
  'Full Name:',
  'Email',
  'Main Number',
  'Company',
  'Role',
  'Operator',
  'Last Active'
]

const formatUsersDGECsvRow = (row: User) => [
  `${row.fullName}`,
  `${row.email}`,
  `${row.mainNumber}`,
  `${row.company}`,
  `${row.role}`,
  `${row.operator?.name}`,
  `${row.lastLogin}`
]

const tableId = 'dge-users-table'

export const UsersViewDGE = () => {
  const { data, pagination, sort, selectedFilters, isFetching, isLoading } = useUsersStoreDGE(
    useShallow((state) => ({
      data: state.data,
      pagination: state.pagination,
      sort: state.sort,
      selectedFilters: state.filters,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching
    }))
  )

  const { fetchData, setFilters, setSort, setPagination, resetState } = useUsersStoreDGE(
    useShallow((state) => ({
      fetchData: state.fetchData,
      setFilters: state.setFilters,
      setSort: state.setSort,
      setPagination: state.setPagination,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    fetchData()
    return resetState
  }, [])

  const handleDeleteUser = async (user: User) => {
    await streamingRestApi.endpoints.users.delete(user.id)
    await fetchData()
  }

  const handleDeleteSelectedUsers = createBulkHandler({
    name: 'Delete Users',
    operation: async (user: User) => {
      await streamingRestApi.endpoints.users.delete(user.id)
    },
    refreshData: fetchData,
    getItemDisplayName: (user) => `${user.email} (ID: ${user.id})`
  })

  return (
    <DataCard
      title="Users"
      headerPostfixActions={
        <>
          <CheckboxMenu tableId={tableId}>
            <CheckboxDeleteSelectedStreamingUserItem onDelete={handleDeleteSelectedUsers} />
          </CheckboxMenu>
          <DownloadCsv
            data={data}
            csvFileName="Users DGE CSV"
            csvHeaders={usersDGECsvHeaders}
            formatCsvRow={formatUsersDGECsvRow}
          />
          <UserFormButton.DGE onSubmit={fetchData} />
        </>
      }
    >
      <FilterableTable
        data={data}
        sort={sort}
        tableId={tableId}
        loading={isLoading}
        fetching={isFetching}
        pagination={pagination}
        filters={DGE_TABLE_FILTERS}
        columns={DGE_TABLE_COLUMNS}
        selectedFilters={selectedFilters}
        onPaginationChange={setPagination}
        onFiltersChange={setFilters}
        onSortChange={setSort}
        withCheckboxes
        rowActions={
          <ActionsMenu>
            <ActionFormItem
              getForm={(row: User) => <DGEUserForm defaultValues={row} onSubmit={fetchData} />}
              displaySeparately
            />
            <ActionDeleteStreamingUserItem onDelete={handleDeleteUser} />
          </ActionsMenu>
        }
      />
    </DataCard>
  )
}
